/* eslint-disable no-console */
import { CHATKIT_CONFIGS } from './configs';
import { extractContextFromDataset } from '../common/helpers';
import { loadChatkitScript } from './scriptLoader';
import { fetchToken } from './tokenService';

const widgets = new Map();

let flowType = null;

function _getConfig(container) {
  const type = container.dataset.chatkitType || flowType;
  const config = { ...CHATKIT_CONFIGS[type] };

  const dynamicContext = extractContextFromDataset(container);
  if (Object.keys(dynamicContext).length > 0) {
    config.context = { ...config.context, ...dynamicContext };
  }

  return config;
}

async function _prepareConfigAndToken(container) {
  if (container.dataset.chatkitType) {
    const config = _getConfig(container);
    const tokenData = await fetchToken(config.context);
    return { config, tokenData };
  }

  const dynamicContext = extractContextFromDataset(container);
  const tokenData = await fetchToken(dynamicContext);
  if (!tokenData) return null;

  flowType = tokenData.flowType;
  const config = _getConfig(container);
  return { config, tokenData };
}

export async function initializeOpenAIWidget(container) {
  await loadChatkitScript();

  const kit = container.querySelector('openai-chatkit');
  if (!kit) return;

  const widgetId = container.id;
  if (widgets.has(widgetId) && widgets.get(widgetId).isInitialized) {
    return;
  }

  if (kit.shadowRoot && kit.shadowRoot.querySelector('iframe')) {
    widgets.set(widgetId, { isInitialized: true });
    return;
  }

  const result = await _prepareConfigAndToken(container);
  const { config, tokenData } = result;

  try {
    const userSettings = document.querySelector('[data-controller="user-account-settings"]');
    const appearanceMode = userSettings?.dataset?.userAccountSettingsThemeValue || 'light';

    kit.setOptions({
      api: {
        async getClientSecret(existing) {
          if (existing) {
            const newTokenData = await fetchToken(config.context);
            if (newTokenData) return newTokenData.token;
            throw new Error('Failed to refresh ChatKit token');
          }
          return tokenData.token;
        }
      },
      theme: {
        colorScheme: appearanceMode
      },
      composer: {
        placeholder: config.placeholder,
        attachments: config.attachments
      },
      startScreen: {
        greeting: config.greeting,
        prompts: config.prompts
      }
    });

    kit.addEventListener('chatkit.response.end', () => {
      document.dispatchEvent(new CustomEvent('chatkit.response.end', { bubbles: true }));
    });

    widgets.set(widgetId, { isInitialized: true, config });
  } catch (error) {
    console.error('Error initializing ChatKit:', error);
  }
}

export function destroyOpenAIWidget(widgetId) {
  const container = document.getElementById(widgetId);
  if (!container) return;

  const kit = container.querySelector('openai-chatkit');
  if (kit && kit.shadowRoot) {
    kit.shadowRoot.innerHTML = '';
  }

  widgets.delete(widgetId);
}

export function destroyAllOpenAIWidgets() {
  widgets.forEach((_, widgetId) => {
    destroyOpenAIWidget(widgetId);
  });
}
